// ============================================
// Promise Keeper — Firestore Service
//
// CRUD for the promise_keeper collection.
// Tracks live patient-facing commitments.
// ============================================

import {
  collection, doc, setDoc, getDocs, query,
  orderBy, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { PatientPromise, PromiseStatus, CreatePromiseInput } from '../../types/promise';
import { C_PROMISE_KEEPER } from '../../constants/collections';

const COLLECTION = C_PROMISE_KEEPER;
const promisesCol = () => collection(db, COLLECTION);
const promiseDoc = (id: string) => doc(db, COLLECTION, id);

// ── Helpers ──

function generateId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `PRM-${ts}-${rand}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}

// ── Create ──

export async function createPromise(input: CreatePromiseInput): Promise<string> {
  const id = generateId();
  const now = new Date().toISOString();

  const promise: PatientPromise = {
    id,
    promise: input.promise,
    context: input.context,
    patientId: input.patientId,
    patientLabel: input.patientLabel,
    source: input.source,
    status: 'pending',
    deadline: input.deadline,
    resolution: '',
    createdAt: now,
    updatedAt: now,
    metadata: input.metadata,
  };

  await setDoc(promiseDoc(id), stripUndefined({
    ...promise,
    _createdAt: Timestamp.now(),
    _updatedAt: Timestamp.now(),
  }));

  return id;
}

// ── Read ──

export async function getAllPromises(): Promise<PatientPromise[]> {
  const q = query(promisesCol(), orderBy('_createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as PatientPromise);
}

export async function getPromisesByStatus(status: PromiseStatus): Promise<PatientPromise[]> {
  const q = query(promisesCol(), where('status', '==', status), orderBy('_createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as PatientPromise);
}

// ── Update Status ──

export async function updatePromiseStatus(
  id: string,
  status: PromiseStatus,
  resolution: string,
): Promise<void> {
  await setDoc(promiseDoc(id), stripUndefined({
    status,
    resolution,
    updatedAt: new Date().toISOString(),
    _updatedAt: Timestamp.now(),
  }), { merge: true });
}

// ── Checkout Integration ──

export async function registerCheckoutPromise(
  orderId: string,
  customerEmail: string,
  bundleName?: string,
): Promise<string> {
  const deadlineDate = new Date();
  deadlineDate.setDate(deadlineDate.getDate() + 5); // 5 business days

  return createPromise({
    promise: `Your ${bundleName || 'supplement protocol'} will ship within 3–5 business days.`,
    context: `Checkout confirmation — Order ${orderId}`,
    patientId: customerEmail,
    patientLabel: customerEmail,
    source: 'checkout',
    deadline: deadlineDate.toISOString(),
    metadata: { orderId },
  });
}
